<template>
  <v-row fill-height>
    <v-col cols="6" md="6" sm="6" xs="6" v-if="displayFiles.length < 1">
      <file-example data-test-file-example :example="example"></file-example>
    </v-col>
    <v-col cols="6" md="6" sm="6" xs="6" v-for="(item, i) in displayFiles" v-bind:key="i">
      <file-upload data-test-file-uploaded :item="item" v-on:deleted="handleDelete"></file-upload>
      <slot name="file" :item="item" />
    </v-col>
    <v-col cols="6" md="6" sm="6" xs="6">
      <image-upload-button
        ref="uploadButton"
        :text="text"
        :textAlt="textAlt"
        :isUploaded="files && files.length > 0"
        :isLoading="isLoading"
        @onDrop="onFileDrag"
        @click="
          () => {
            if (!isCaptureEnvironment) click();
          }
        "
      />

      <slot name="upload" />
    </v-col>

    <input
      data-test-report-image-upload
      name="file"
      type="file"
      ref="uploader"
      style="display: none"
      :accept="inputTypes"
      @change="onFileInputEvent"
    />
    <input
      data-test-report-image-upload
      name="file"
      type="file"
      ref="uploaderWithoutFileSelectionDialog"
      style="display: none"
      :accept="imageInputTypes"
      capture="environment"
      @change="onFileInputEvent"
    />

    <pdf-upload-dialog
      @closePdfDialog="closePdfDialog"
      @onImageExport="onImageExport"
      :isDialog="pdfDialog"
      :pdf="pdf"
      v-if="isPdfUploadEnabled"
    />

    <camera-dialog
      v-if="isCameraDialogActive"
      ref="dialog"
      :instruction="instruction"
      :asset="asset"
      :handleUploadCb="handleInput"
      :handleDeleteCb="deleted"
      :images="files.map((file) => file.file)"
      v-on:close-camera-dialog="isCameraDialogActive = false"
    />
    <!-- @vue-ignore -->
    <camera-bottom-sheet
      ref="sheet"
      v-on:camera-clicked="isCameraDialogActive = true"
      v-on:library-clicked="$refs.uploader.click()"
    />

    <debug>
      isCameraDialogActive {{ isCameraDialogActive }} <br />
      isTouch {{ isTouch }} <br />
      <v-btn @click="click">click</v-btn>
      <v-btn @click="rightClick">rightClick</v-btn>
      <v-btn @click="debugLog = ''">Clear</v-btn>

      <v-divider />

      <br />
      debugLog :
      <br />
      <div v-html="debugLog" />

      <v-divider />
    </debug>
  </v-row>
</template>

<script lang="ts">
import CameraBottomSheet from "@/components/camera/CameraBottomSheet.vue";
import CameraDialog from "@/components/camera/CameraDialog.vue";
import FileExample from "@/components/files/FileExample.vue";
import FileUpload from "@/components/files/FileUpload.vue";
import ImageUploadButton from "@/components/files/ImageUploadButton.vue";
import PdfUploadDialog from "@/components/files/PdfUploadDialog.vue";
import PdfViewer from "@/components/files/PdfViewer.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import ReportImageSubmission from "@/mixins/ReportImageSubmissionMixin.vue";
import { IImageUploaded } from "@/models/Image/IImageUploaded";
import { mixins } from "vue-class-component";
import { Component, Prop, Ref } from "vue-property-decorator";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { isMobile } from "@/lib/utility/isMobile";
import Debug from "./Debug.vue";
import Vue from "vue";

@Component({
  components: {
    PdfViewer,
    LayoutReportStep,
    ImageUploadButton,
    FileExample,
    FileUpload,
    CameraDialog,
    CameraBottomSheet,
    PdfUploadDialog,
    Debug
  }
})
/**
 * Uploads images as overview images
 */
export default class FileUploadPreview extends mixins(ReportImageSubmission) {
  readonly LONG_PRESS_DURATION_IN_MS = 750;

  @Ref("sheet") sheet!: CameraBottomSheet;

  @Ref("uploader") uploader!: HTMLInputElement;

  @Ref("uploaderWithoutFileSelectionDialog") uploaderWithoutFileSelectionDialog!: HTMLInputElement;

  @Prop()
  example!: AssetEnum;

  @Prop()
  displayFiles!: IImageUploaded[];

  @Prop({ default: null })
  public maxFiles!: number | null;

  @Prop({ default: 1 })
  public minFiles!: number;

  @Prop()
  onUpload!: (file: File) => Promise<boolean>;

  @Prop()
  onDelete!: (file: File) => Promise<boolean>;

  @Prop()
  asset!: AssetEnum;

  @Prop()
  instruction!: IReportInstructionData;

  /**
   * Text to be displayed on the button card.
   */
  @Prop({ default: "defaultFoto" })
  text: any;

  /**
   * An alternative text to be displayed on @isUploade
   */
  @Prop({ default: "defaultFotoAgain" })
  textAlt: any;

  mouseDownTimer: NodeJS.Timeout | null = null;

  debugLog = "";

  get inputTypes() {
    return ConfigModule.pdfInputTypesString;
  }

  get imageInputTypes() {
    return ConfigModule.imageInputTypes.join(",");
  }

  get files(): IImageUploaded[] {
    return this.displayFiles;
  }

  get isTouch() {
    return isMobile();
  }

  onFileDrag(file: File) {
    this.handleInput(file);
  }

  async handleUpload(file: File): Promise<boolean> {
    await this.onUpload(file);
    return true;
  }

  async handleDelete(file: File): Promise<boolean> {
    await this.onDelete(file);

    return true;
  }

  mounted() {
    if (this.isCaptureEnvironment) {
      if (this.isTouch) {
        (this.$refs.uploadButton as Vue).$el.addEventListener("touchstart", this.onMouseDown);
        (this.$refs.uploadButton as Vue).$el.addEventListener("touchend", this.onMouseUp);
      } else {
        (this.$refs.uploadButton as Vue).$el.addEventListener("mousedown", this.onMouseDown);
        (this.$refs.uploadButton as Vue).$el.addEventListener("mouseup", this.onMouseUp);
      }
    }
  }

  beforeDestroy() {
    if (this.isCaptureEnvironment) {
      if (this.isTouch) {
        (this.$refs.uploadButton as Vue).$el.removeEventListener("touchstart", this.onMouseDown);
        (this.$refs.uploadButton as Vue).$el.removeEventListener("touchend", this.onMouseUp);
      } else {
        (this.$refs.uploadButton as Vue).$el.removeEventListener("mousedown", this.onMouseDown);
        (this.$refs.uploadButton as Vue).$el.removeEventListener("mouseup", this.onMouseUp);
      }
    }
  }

  onMouseDown() {
    this.debugLog += "onMouseDown<br/>";
    this.mouseDownTimer = setTimeout(() => {
      this.debugLog += "Timeout Executed<br/>";
      this.mouseDownTimer = null;
      this.onRightClick();
    }, this.LONG_PRESS_DURATION_IN_MS);
  }

  onMouseUp() {
    this.debugLog += "onMouseUp<br/>";

    if (FeatureModule.isFileSelectionOnShortPressInImageUpload) {
      this.debugLog += "Feature Flag<br/>";
      this.onRightClick();
    } else if (this.mouseDownTimer) {
      this.onClick();
    }

    if (this.mouseDownTimer) {
      this.debugLog += "Timeout Interrupted<br/>";
      clearTimeout(this.mouseDownTimer);
      this.mouseDownTimer = null;
    }
  }

  onRightClick() {
    this.debugLog += "file browser dialog<br/>";
    this.rightClick();
  }

  onClick() {
    this.debugLog += "jump to camera<br/>";
    this.click();
  }
}
</script>

<style scoped>
.drag {
  border-radius: 5px;
  position: absolute;
  z-index: 1;
  background-color: rgba(1, 1, 1, 0.3);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fade {
  animation: fade 1s ease-out;
}
.fadeIn {
  animation: appear 0.5s;
}

@keyframes appear {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fade {
  0% {
    opacity: 0.5;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.9);
  }
}
</style>
